import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExpand, faCompress, faArrowLeft, faChevronLeft, faChevronRight, faVolumeUp, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Header } from './shared';
import { TV_CHANNELS } from '../config/channels';
import { TV_STREAM_BASE } from '../config/constants';
import { useDevice } from '../hooks/useMediaQuery';

const ChannelPlayer: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const { isMobileOrTablet } = useDevice();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showAudioNotice, setShowAudioNotice] = useState(true);

  // Find current channel info
  const currentChannel = TV_CHANNELS.find(ch => ch.id === channelId);
  const currentIndex = TV_CHANNELS.findIndex(ch => ch.id === channelId);
  const prevChannel = currentIndex > 0 ? TV_CHANNELS[currentIndex - 1] : null;
  const nextChannel = currentIndex < TV_CHANNELS.length - 1 ? TV_CHANNELS[currentIndex + 1] : null;

  const iframeSrc = `${TV_STREAM_BASE}/${channelId}.php`;

  // Handle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Auto-hide controls
  const handleMouseMove = useCallback(() => {
    setShowControls(true);

    if (controlsTimeout) {
      clearTimeout(controlsTimeout);
    }

    const timeout = setTimeout(() => {
      if (isFullscreen) {
        setShowControls(false);
      }
    }, 3000);

    setControlsTimeout(timeout);
  }, [isFullscreen, controlsTimeout]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'Escape':
          if (isFullscreen) {
            document.exitFullscreen?.();
          }
          break;
        case 'ArrowLeft':
          if (prevChannel) {
            navigate(`/channel/${prevChannel.id}`);
          }
          break;
        case 'ArrowRight':
          if (nextChannel) {
            navigate(`/channel/${nextChannel.id}`);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen, isFullscreen, navigate, prevChannel, nextChannel]);

  // Block popups
  useEffect(() => {
    const originalOpen = window.open;
    window.open = () => null;

    return () => {
      window.open = originalOpen;
    };
  }, []);

  // Auto-hide audio notice after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAudioNotice(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-dark-950">
      {!isFullscreen && <Header showSearch={false} />}

      {/* Main Content */}
      <main className={`${isFullscreen ? '' : 'max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8'}`}>

        {/* Back Button & Channel Info (not in fullscreen) */}
        {!isFullscreen && (
          <div className="flex items-center justify-between mb-6 animate-fade-in">
            <button
              onClick={() => navigate('/tv')}
              className="flex items-center gap-2 text-gray-400 hover:text-white
                transition-colors group"
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                className="transform group-hover:-translate-x-1 transition-transform"
              />
              <span>Back to Channels</span>
            </button>

            {currentChannel && (
              <div className="flex items-center gap-3">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
                <span className="text-sm font-semibold text-gray-300">LIVE</span>
              </div>
            )}
          </div>
        )}

        {/* Channel Header (not in fullscreen) */}
        {!isFullscreen && currentChannel && (
          <div className="mb-6 animate-fade-in" style={{ animationDelay: '100ms' }}>
            <div className="flex items-center gap-4 mb-2">
              <currentChannel.logo className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                  {currentChannel.name}
                </h1>
                <p className="text-gray-400">{currentChannel.category}</p>
              </div>
            </div>
          </div>
        )}

        {/* Video Player Container */}
        <div
          ref={containerRef}
          onMouseMove={handleMouseMove}
          className={`
            relative bg-black rounded-2xl overflow-hidden
            shadow-2xl shadow-black/50
            ${isFullscreen
              ? 'fixed inset-0 z-50 rounded-none'
              : 'aspect-video'
            }
          `}
        >
          {/* Video iframe */}
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title={currentChannel?.name || 'Live TV'}
            className="w-full h-full"
            allowFullScreen
            allow="autoplay; encrypted-media; fullscreen"
          />


          {/* Top Bar - Only in Fullscreen */}
          {isFullscreen && (
            <div className={`
              absolute top-0 left-0 right-0 p-4 sm:p-6 z-10
              bg-gradient-to-b from-black/80 to-transparent
              flex items-center justify-between
              transition-opacity duration-300
              ${showControls ? 'opacity-100' : 'opacity-0'}
            `}>
              <button
                onClick={() => document.exitFullscreen?.()}
                className="flex items-center gap-2 text-white/80 hover:text-white
                  transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span className="hidden sm:inline">Exit Fullscreen</span>
              </button>

              {currentChannel && (
                <div className="flex items-center gap-3 text-white">
                  <currentChannel.logo className="w-6 h-6 text-white" />
                  <span className="font-bold text-lg">{currentChannel.name}</span>
                  <span className="relative flex h-2.5 w-2.5 ml-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Bottom Controls */}
          <div className={`
            absolute bottom-16 left-0 right-0 px-3 py-2 z-10
            transition-opacity duration-300
            ${showControls ? 'opacity-100' : 'opacity-0'}
          `}>
            <div className="flex items-center justify-between max-w-4xl mx-auto">
              {/* Channel Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => prevChannel && navigate(`/channel/${prevChannel.id}`)}
                  disabled={!prevChannel}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 
                    rounded-lg bg-black/60 backdrop-blur-sm
                    text-white text-sm disabled:opacity-30 disabled:cursor-not-allowed
                    hover:bg-black/80 transition-all border border-white/20"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                  <span className="hidden sm:inline text-xs">
                    {prevChannel?.name || 'Previous'}
                  </span>
                </button>

                <button
                  onClick={() => nextChannel && navigate(`/channel/${nextChannel.id}`)}
                  disabled={!nextChannel}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 
                    rounded-lg bg-black/60 backdrop-blur-sm
                    text-white text-sm disabled:opacity-30 disabled:cursor-not-allowed
                    hover:bg-black/80 transition-all border border-white/20"
                >
                  <span className="hidden sm:inline text-xs">
                    {nextChannel?.name || 'Next'}
                  </span>
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>

              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg bg-black/60 backdrop-blur-sm
                  text-white hover:bg-black/80 transition-all border border-white/20"
              >
                <FontAwesomeIcon
                  icon={isFullscreen ? faCompress : faExpand}
                  className="text-sm"
                />
              </button>
            </div>
          </div>

          {/* Loading State */}
          <div className="absolute inset-0 flex items-center justify-center
            bg-dark-900 pointer-events-none opacity-0 
            [iframe:not([src])_+_&]:opacity-100">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent 
                rounded-full animate-spin mb-4" />
              <p className="text-gray-400">Loading channel...</p>
            </div>
          </div>

          {/* Audio Help Notice */}
          {showAudioNotice && (
            <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30
              max-w-md mx-4 animate-fade-in">
              <div className="bg-amber-500/95 backdrop-blur-sm rounded-xl p-4 shadow-2xl
                border border-amber-400">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-white text-xl mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-white font-bold text-sm mb-1">Need Sound?</h4>
                    <p className="text-white/90 text-xs leading-relaxed">
                      Click the video player and look for the <FontAwesomeIcon icon={faVolumeUp} className="mx-1" />
                      unmute button (usually bottom-left corner)
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAudioNotice(false)}
                    className="text-white/70 hover:text-white transition-colors"
                    aria-label="Close notice"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Channel Quick Switch (not in fullscreen) */}
        {!isFullscreen && (
          <section className="mt-12 animate-fade-in" style={{ animationDelay: '200ms' }}>
            <h2 className="text-xl sm:text-2xl font-bold mb-6">
              Other Channels
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {TV_CHANNELS
                .filter(ch => ch.id !== channelId)
                .slice(0, 6)
                .map((channel, index) => (
                  <button
                    key={channel.id}
                    onClick={() => navigate(`/channel/${channel.id}`)}
                    className={`
                      relative p-4 sm:p-6 rounded-xl
                      bg-gradient-to-br ${channel.color}
                      text-white font-semibold text-center
                      transform transition-all duration-300
                      hover:scale-105 hover:shadow-xl
                      animate-fade-in
                    `}
                    style={{ animationDelay: `${(index + 4) * 50}ms` }}
                  >
                    <channel.logo className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 text-white" />
                    <span className="text-sm sm:text-base">{channel.name}</span>
                  </button>
                ))}
            </div>
          </section>
        )}

        {/* Keyboard Shortcuts Info (not in fullscreen) */}
        {!isFullscreen && !isMobileOrTablet && (
          <div className="mt-12 p-6 rounded-xl bg-dark-800/50 border border-white/5
            animate-fade-in" style={{ animationDelay: '300ms' }}>
            <h3 className="font-semibold mb-4 text-gray-300">Keyboard Shortcuts</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-dark-700 rounded text-gray-300">F</kbd>
                <span>Toggle Fullscreen</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-dark-700 rounded text-gray-300">←</kbd>
                <span>Previous Channel</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-dark-700 rounded text-gray-300">→</kbd>
                <span>Next Channel</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-dark-700 rounded text-gray-300">Esc</kbd>
                <span>Exit Fullscreen</span>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Spacer */}
      {!isFullscreen && <div className="h-16" />}
    </div>
  );
};

export default ChannelPlayer;
